import { useEffect, useLayoutEffect, useRef, useState, type UIEvent } from "react";
import { useApiClient } from "../../api/ApiProvider";
import type { LogStreamEndReason, LogStreamStatus } from "../../api/client";
import { Spinner } from "../../components/AsyncStates";
import { Button } from "../../components/Button";
import { appendBounded, type BufferedLine } from "./logBuffer";
import "./live-log.css";

/** Default backfill on (re)start — matches the SSE contract's `tail` param. */
const TAIL_LINES = 200;

/** Bounded client buffer (charter P-6 spirit): a chatty container can't grow
 * memory/DOM unbounded — the oldest lines are dropped as new ones arrive. */
const MAX_BUFFERED_LINES = 2000;

/** How close to the bottom (px) counts as "at the bottom" for resuming
 * auto-scroll after the operator scrolls back down. */
const AUTO_SCROLL_RESUME_PX = 32;

const END_REASON_COPY: Record<LogStreamEndReason, string> = {
  stopped: "the container stopped.",
  truncated: "the server closed the connection (older output was dropped to keep up).",
  error: "repeated connection errors. Try starting it again in a moment.",
};

interface LiveLogPanelProps {
  fleetId: string;
  nodeId: string;
  containerId: string;
}

/**
 * Live-tail mode for the Logs tab (C-8a): opens `client.openLogStream(...)`
 * on toggle-on and tears it down (`handle.close()`) on toggle-off, unmount,
 * or a change of target container — the effect's cleanup covers all three
 * (including "navigated away," since that unmounts this component).
 */
export function LiveLogPanel({ fleetId, nodeId, containerId }: LiveLogPanelProps) {
  const client = useApiClient();
  const [enabled, setEnabled] = useState(false);
  const [status, setStatus] = useState<LogStreamStatus>("closed");
  const [lines, setLines] = useState<BufferedLine[]>([]);
  const [endReason, setEndReason] = useState<LogStreamEndReason | null>(null);
  const [autoScroll, setAutoScroll] = useState(true);

  const nextLineId = useRef(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!enabled) return;

    const handle = client.openLogStream({ fleetId, nodeId, containerId, tail: TAIL_LINES });

    // FE-H1: a chatty container can emit far faster than React (or the DOM)
    // wants to re-render — every `onLine` call used to trigger its own
    // `setLines`, so the whole ≤2000-line buffer re-rendered per SSE line
    // rather than per frame. Instead, incoming lines land in this plain
    // array (mutated outside React, not state) and a single
    // requestAnimationFrame flushes whatever has queued since the last
    // paint into one state update — at most one render per frame regardless
    // of line rate. `pending` is also capped so a backgrounded tab (rAF
    // doesn't run while hidden) can't grow it unbounded between flushes
    // (P-6).
    let pending: BufferedLine[] = [];
    let rafHandle: number | null = null;

    function flush() {
      rafHandle = null;
      if (pending.length === 0) return;
      const incoming = pending;
      pending = [];
      // FE-M4: `incoming` was already fully built (ids minted, below) before
      // this updater runs — the updater itself only combines two arrays it
      // already has, no ref mutation or id-minting inside it, so it stays
      // pure under StrictMode's double-invoke.
      setLines((prev) => appendBounded(prev, incoming, MAX_BUFFERED_LINES));
    }

    const unsubLine = handle.onLine((line) => {
      // Ids are minted here, in the event handler — not inside the setLines
      // updater above (FE-M4) — so this ref mutation happens exactly once
      // per real line, never doubled by a React double-invoke.
      nextLineId.current += 1;
      pending.push({ id: nextLineId.current, stream: line.stream, message: line.message });
      if (pending.length > MAX_BUFFERED_LINES) {
        pending = pending.slice(pending.length - MAX_BUFFERED_LINES);
      }
      if (rafHandle === null) {
        rafHandle = requestAnimationFrame(flush);
      }
    });
    const unsubStatus = handle.onStatus(setStatus);
    const unsubEnd = handle.onEnd((reason) => {
      flush(); // don't drop the last coalesced batch on a clean end
      setEndReason(reason);
      setStatus("closed");
      setEnabled(false);
    });

    return () => {
      unsubLine();
      unsubStatus();
      unsubEnd();
      handle.close();
      if (rafHandle !== null) {
        cancelAnimationFrame(rafHandle);
        rafHandle = null;
      }
      pending = [];
    };
    // Reopens whenever the operator toggles it on/off, or the target
    // container changes out from under an active stream. `client` is stable
    // for the app's lifetime (see ApiProvider).
  }, [enabled, client, fleetId, nodeId, containerId]);

  // Auto-scroll to the newest line unless the operator has scrolled up to
  // read history (see handleScroll) — runs before paint so there's no
  // visible jump on a fast-arriving line.
  useLayoutEffect(() => {
    if (!autoScroll) return;
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [lines, autoScroll]);

  function handleScroll(event: UIEvent<HTMLDivElement>) {
    const el = event.currentTarget;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setAutoScroll(distanceFromBottom <= AUTO_SCROLL_RESUME_PX);
  }

  function handleJumpToLatest() {
    setAutoScroll(true);
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }

  function handleToggle() {
    if (enabled) {
      setEnabled(false);
      setStatus("closed");
      return;
    }
    setLines([]);
    setEndReason(null);
    setAutoScroll(true);
    setStatus("connecting");
    setEnabled(true);
  }

  function handleClear() {
    setLines([]);
  }

  const statusLabel =
    status === "connecting" ? "Connecting…" : status === "reconnecting" ? "Reconnecting…" : enabled ? "Live" : null;
  const isBusyStatus = status === "connecting" || status === "reconnecting";

  return (
    <div className="live-log">
      <div className="live-log__toolbar">
        <div className="live-log__toolbar-group">
          <Button variant={enabled ? "danger" : "primary"} onClick={handleToggle}>
            {enabled ? "Stop live logs" : "Start live logs"}
          </Button>
          <Button variant="ghost" onClick={handleClear} disabled={lines.length === 0}>
            Clear buffer
          </Button>
        </div>
        {statusLabel && (
          <span className={`live-log__status live-log__status--${status}`} role="status">
            {isBusyStatus && <Spinner label={statusLabel} />}
            {statusLabel}
          </span>
        )}
      </div>

      {endReason && (
        <p className="live-log__end-notice" role="status">
          Stream ended: {END_REASON_COPY[endReason]}
        </p>
      )}

      <div className="live-log__panel-wrap">
        <div
          className="live-log__panel"
          role="log"
          aria-label="Live container log output"
          // role="log" implies aria-live="polite" by default, which would
          // have assistive tech announce every incoming line — much too
          // noisy for a fast-moving stream. Off by default; the region is
          // still keyboard-reachable/scrollable (tabIndex) and its content
          // is readable on demand, just not auto-announced.
          aria-live="off"
          // WCAG 2.1.1/2.4.3 scrollable-region technique (SCR29): tabIndex
          // makes this reachable by keyboard so arrow/Page keys can scroll
          // it, even though jsx-a11y's interactive-role list doesn't
          // consider "log" one that needs focus support.
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={0}
          ref={scrollRef}
          onScroll={handleScroll}
        >
          {lines.length === 0 ? (
            <p className="live-log__empty">
              {enabled ? "Waiting for output…" : "Live logs are off. Start them to tail this container in real time."}
            </p>
          ) : (
            lines.map((line) => (
              <div key={line.id} className={`live-log__line live-log__line--${line.stream}`}>
                <span className="live-log__line-tag">{line.stream === "stderr" ? "ERR" : "OUT"}</span>
                <span className="live-log__line-message">{line.message}</span>
              </div>
            ))
          )}
        </div>
        {!autoScroll && lines.length > 0 && (
          <button type="button" className="live-log__jump" onClick={handleJumpToLatest}>
            Jump to latest ↓
          </button>
        )}
      </div>

      <p className="live-log__hint">
        Streams the last {TAIL_LINES} lines, then live. Kept to the most recent{" "}
        {MAX_BUFFERED_LINES.toLocaleString()} lines in this panel — older output scrolls out, it isn't stored here.
      </p>
    </div>
  );
}
